<template>
  <div data-private class="w-full text-sm relative">
    <!-- Saving indicator -->
    <div v-if="isSaving" class="absolute top-0 right-0 bg-primary-500 text-white px-3 py-1 rounded-md shadow-lg flex items-center gap-2">
      <Spinning_Loader class="w-3 h-3" />
      <span>Saving...</span>
    </div>

    <!-- Save error -->
    <div v-if="saveError" class="absolute top-0 right-0 bg-red-500 text-white px-3 py-1 rounded-md shadow-lg">
      {{ saveError }}
    </div>

    <div v-if="loading" class="w-40 h-40 items-center justify-center flex">
      <Spinning_Loader/>
    </div>

    <div v-else class="w-full p-4 bg-gray-50 mt-2 rounded-lg border border-gray-200 shadow-sm">
      <!-- Patient Info Section -->
      <div data-private class="mb-4 pb-4 border-b border-gray-200">
        <div class="space-y-2">
          <div class="flex items-center">
            <span class="font-semibold text-gray-700 mr-2">Patient:</span>
            <span class="sensitive text-gray-900">{{ task.metadata.bene_first_name }} {{ task.metadata.bene_last_name }}</span>
          </div>
          <div class="flex items-center">
            <span class="font-semibold text-gray-700 mr-2">Patient ID:</span>
            <span class="sensitive text-gray-900">{{ task.metadata.patient_identifier }}</span>
          </div>
          <div v-if="task.metadata.bene_dob" class="flex items-center">
            <span class="font-semibold text-gray-700 mr-2">DOB:</span>
            <span class="sensitive text-gray-900">{{ task.metadata.bene_dob?.split(' ')[0] }}</span>
          </div>
          <div class="flex items-center">
            <span class="font-semibold text-gray-700 mr-2">Claim Date:</span>
            <span class="text-gray-900">{{ task.metadata['clm_thru_dt'] }}</span>
          </div>
          <div v-if="task.metadata['rendering_npis']" class="flex items-start">
            <span class="font-semibold text-gray-700 mr-2">NPIs:</span>
            <div class="flex flex-wrap gap-1.5">
              <span v-for="npi in task.metadata['rendering_npis']" :key="npi"
                    class="sensitive inline-block rounded-md border border-primary-300 px-2 py-0.5 bg-primary-100 text-primary-800 font-medium">
                {{ npi }}
              </span>
            </div>
          </div>
          <div class="flex items-center">
            <span class="font-semibold text-gray-700 mr-2">TIN:</span>
            <span class="text-gray-900 sensitive">{{ task.metadata['tin'] }}</span>
          </div>
        </div>
      </div>

      <!-- HCC Codes Section -->
      <div v-if="Object.keys(hcc_model_codes).length > 0" class="mb-4">
        <div class="font-semibold text-gray-900 mb-3">All Patient HCCs Billed in Year:</div>
        <div class="space-y-2.5">
          <div v-for="(value, key) in hcc_model_codes" :key="key">
            <div v-if="key !== 22 && value.length > 0" class="flex items-center gap-2">
              <div class="font-semibold text-gray-700 min-w-[2rem]">v{{key}}:</div>
              <div class="flex flex-wrap items-center gap-1.5">
                <div v-for="hcc in [...value].sort((a, b) => a - b)" :key="hcc"
                     class="rounded-md border bg-amber-100 border-amber-400 px-2 py-1 text-amber-900 font-semibold">
                  {{hcc}}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ICD Codes Section -->
      <div v-if="dx_codes_billed.length > 0">
        <div class="font-semibold text-gray-900 mb-3">Current ICD Codes Billed During this Encounter</div>
        <div class="flex flex-wrap items-center gap-1.5">
          <div v-for="code in [...dx_codes_billed].sort()" :key="code"
               class="rounded-md bg-blue-100 border-blue-400 border px-2 py-1 text-blue-900 font-semibold">
            {{code}}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Spinning_Loader from '@/components/widgetcomponents/Viz_Elements/Spinning_Loader.vue';

export default {
  components: {
    Spinning_Loader
  },
  props: {
    task: {
      type: Object,
      required: true
    }
  },
  emits: ['can_submit'],
  data() {
    return {
      clm_data: [],
      loading: false
    };
  },
  computed: {
    hcc_model_codes() {
      if (this.clm_data.length > 0 && this.clm_data[0].model_hcc_codes) {
        try {
          const model_hcc_codes = JSON.parse(this.clm_data[0].model_hcc_codes);
          return model_hcc_codes || {};
        } catch (error) {
          console.error('Error parsing model_hcc_codes:', error);
          return {};
        }
      } else {
        return {};
      }
    },

    dx_codes_billed() {
      if (this.clm_data.length > 0) {
        if (!this.clm_data[0].dx_codes_billed) {
          return [];
        }

        try {
          const dx_codes_billed = JSON.parse(this.clm_data[0].dx_codes_billed);
          return Array.isArray(dx_codes_billed) ? dx_codes_billed : [];
        } catch (error) {
          console.error('Error parsing dx_codes_billed:', error);
          return [];
        }
      }
      return [];
    }
  },
  methods: {
    async fetchRelevantClaims() {
      if (!this.task.metadata.patient_identifier || !this.task.metadata.clm_thru_dt) {
        console.error('Error fetching encounter data: Missing patient identifier or claim through date')
        this.show_data = false
        return
      }

      this.loading = true;

      const request_data = {
        metric_id: 30,
        metric_insight_id: 1,
        insight_type: 'task_insight',
        sql_filters: {
          bene_mbi_id: this.task.metadata['patient_identifier'],
          clm_thru_dt: this.task.metadata['clm_thru_dt'],
          tin: this.task.metadata['tin']
        }
      }

      try {
        const response = await axios.post(
          'api/returnmetricinsight/',
          request_data,
        )

        if (response?.data?.length > 0) {
          this.clm_data = response.data
          this.show_data = true
        } else {
          this.show_data = false
        }
      } catch (error) {
        console.error('Error fetching encounter data:', error)
        this.show_data = false
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    this.fetchRelevantClaims();
    this.$emit('can_submit', true, '');
  }
};
</script>
